/**
 * ClarifyCard — the docked clarifying-question round (spec 2026-10-12,
 * "Frontend"). It stands in the composer's slot once the lead-in turn is done,
 * asks one question at a time, and hands back exactly one outcome: answers
 * («Готово»), a skip, a smart-rec pick, or a dismissal (✕ / Escape).
 *
 * DRAFTED STATE (GUARDRAILS rule 10; DESIGN-SYSTEM.md drift item 8): the
 * prototype has no question card. Drafted from BottomSheet's radius and shadow,
 * the onboarding budget radio rows and mono header chips, on `--g-*` tokens —
 * or the `--gd-*` equivalents inside the ≥1200px design frame (skin.ts).
 *
 * Interaction:
 * - single-select: a tap selects, then advances after CLARIFY_AUTO_ADVANCE_MS
 *   = MOTION.dur.item (0.28 s), the house "one element settles" duration — long
 *   enough for the 150 ms fill to read as chosen before the 0.22 s question
 *   cross-fade starts, short enough to feel automatic. The LAST question never
 *   auto-submits: only «Готово» does.
 * - multi-select: checkboxes and «Далее».
 * - ‹ › step questions; moving on without an answer skips that question.
 * - «Готово» with nothing answered is a skip (C12: the summary is the skip label).
 * - smart-rec rounds (R1/R2): one tap IS the answer (`onPickOption`); no
 *   «Готово», and the server sends allow_custom/allow_dont_know false.
 * - while questions change (the 0.22 s cross-fade) the rows take no pointer
 *   input, the outgoing question is inert, «Далее»/«Готово» ignores clicks,
 *   and any handler of the outgoing question is a no-op; each new question
 *   starts scrolled to the top.
 *
 * Accessibility: a region named by the current question's legend; each
 * question a fieldset; single-select a radiogroup with roving focus;
 * multi-select checkboxes; focus moves to the first option when the card docks
 * on fine pointers only (MessageInput.tsx:396-408's rule — a touch keyboard
 * must not pop), and follows into the next question only if it was inside the
 * card; «Вопрос n из N» is announced politely; every target is ≥44px.
 */
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { useAssistantDesign } from "@/features/chat/useDesignFrame";
import { iconProps } from "@/lib/icons";
import { MOTION } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

import { cardMotion, questionVariants } from "./card-motion";
import { ClarifyQuestionView } from "./ClarifyQuestionView";
import {
  applyChoice,
  questionDraft,
  setCustomText,
  toAnswerInputs,
  withAnswer,
  withIndex,
  type ClarifyChoice,
  type ClarifyDraft,
} from "./draft";
import { isSmartRecKind } from "./parse";
import { clarifySkin } from "./skin";
import type { ClarifyAnswerInput, ClarifyRoundEvent, ClarifyRoundOption } from "./types";

/** The beat between a single-select tap and the next question (ms). */
export const CLARIFY_AUTO_ADVANCE_MS = Math.round(MOTION.dur.item * 1000);

/**
 * `chat.clarify.custom_max_chars` default (spec "Settings") — the cap when a
 * round carries no `custom_max_chars` (index amendment A3: the server sends the
 * live value on every round; old payloads and fixtures may omit it).
 */
export const CLARIFY_CUSTOM_MAX_CHARS_DEFAULT = 200;

export interface ClarifyCardProps {
  round: ClarifyRoundEvent;
  /** Controlled draft — memory only (the chat store holds it, W3). */
  draft: ClarifyDraft;
  onDraftChange: (draft: ClarifyDraft) => void;
  /** «Готово» with at least one answer (ordinary and quote rounds). */
  onSubmit: (answers: ClarifyAnswerInput[]) => void;
  /** The round-level escape, and «Готово» with nothing answered. */
  onSkip: () => void;
  /** ✕ or Escape. */
  onDismiss: () => void;
  /** Smart-rec rounds only: the tapped option IS the answer. */
  onPickOption?: (option: ClarifyRoundOption) => void;
  /** A need fetch is in flight: rows and the escape are inert. */
  disabled?: boolean;
}

function isFinePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: fine)").matches
  );
}

interface QuestionSlideProps {
  direction: number;
  reduced: boolean;
  children: ReactNode;
}

/**
 * One question's slide, a direct child of AnimatePresence. While it fades out
 * it is `inert`: out of the tab order and the accessibility tree and deaf to
 * taps, so the outgoing fieldset never reads, or answers, as a second question.
 */
function QuestionSlide({ direction, reduced, children }: QuestionSlideProps): ReactElement {
  const isPresent = useIsPresent();
  return (
    <motion.div
      inert={!isPresent}
      className="col-start-1 row-start-1 min-w-0"
      custom={direction}
      variants={questionVariants(reduced)}
      initial="enter"
      animate="center"
      exit="exit"
    >
      {children}
    </motion.div>
  );
}

export function ClarifyCard({
  round,
  draft,
  onDraftChange,
  onSubmit,
  onSkip,
  onDismiss,
  onPickOption,
  disabled = false,
}: ClarifyCardProps): ReactElement {
  // The live «Свой ответ» cap rides the round (index amendment A3).
  const customMaxChars = round.custom_max_chars ?? CLARIFY_CUSTOM_MAX_CHARS_DEFAULT;
  const { t } = useTranslation("chat");
  const skin = clarifySkin(useAssistantDesign());
  const { reduced } = useGardenMotion();
  const baseId = useId();
  const cardRef = useRef<HTMLElement | null>(null);
  const doneRef = useRef<HTMLButtonElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const advanceTimer = useRef<number | null>(null);
  // The latest draft and setter, for the auto-advance timer's callback.
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const onDraftChangeRef = useRef(onDraftChange);
  onDraftChangeRef.current = onDraftChange;
  const [direction, setDirection] = useState(1);
  // On dock: fine pointers only. Afterwards: only if focus was in the card.
  const [focusQuestion, setFocusQuestion] = useState(isFinePointer);
  const [announcement, setAnnouncement] = useState("");
  // True from a question change until the outgoing question has faded out
  // (AnimatePresence's onExitComplete). Meanwhile both questions share the
  // grid cell — the incoming one on top at low opacity — so pointers are off
  // and «Далее»/«Готово» ignores clicks.
  const [moving, setMoving] = useState(false);

  const total = round.questions.length;
  const clampIndex = (value: number): number => Math.min(Math.max(value, 0), total - 1);
  const index = clampIndex(draft.index);
  const isLast = index === total - 1;
  const smartRec = isSmartRecKind(round.kind);
  /**
   * Whether `questionId` is the question on screen now — false for the one
   * fading out, whose handlers are a render stale (its `index`, its `isLast`).
   */
  const isCurrent = (questionId: string): boolean =>
    round.questions[clampIndex(draftRef.current.index)]?.id === questionId;

  const clearAdvance = useCallback((): void => {
    if (advanceTimer.current !== null) {
      window.clearTimeout(advanceTimer.current);
      advanceTimer.current = null;
    }
  }, []);
  useEffect(() => clearAdvance, [clearAdvance]);

  const goTo = useCallback(
    (next: number): void => {
      clearAdvance();
      const current = draftRef.current;
      const bounded = Math.min(Math.max(next, 0), total - 1);
      if (bounded === current.index) return;
      const card = cardRef.current;
      setFocusQuestion(card !== null && card.contains(document.activeElement));
      setDirection(bounded > current.index ? 1 : -1);
      setMoving(true);
      onDraftChangeRef.current(withIndex(current, bounded));
    },
    [clearAdvance, total],
  );

  // Each question starts at the top of the scroll area (a long list scrolled
  // down would otherwise open the next question with its legend off-screen).
  // Before paint, so no frame shows the new question mid-scroll.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (scroll !== null) scroll.scrollTop = 0;
  }, [index]);

  useEffect(() => {
    if (total < 2) return undefined;
    // Deferred one macrotask so the live region exists, stable, before its
    // text changes (the GuestGateCard.tsx:74-82 rule).
    const timer = window.setTimeout(() => {
      setAnnouncement(t("clarify.announce", { n: index + 1, total }));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [index, total, t]);

  const question = round.questions[index];
  if (question === undefined) return <></>;
  const legendId = `${baseId}-legend-${question.id}`;

  const choose = (choice: ClarifyChoice): void => {
    // A tap on the question fading out would record an answer to it and arm an
    // advance from its stale index (‹ then a tap jumped 0 → 2).
    if (disabled || !isCurrent(question.id)) return;
    if (smartRec) {
      if (choice.kind !== "option") return;
      const option = question.options.find((candidate) => candidate.id === choice.optionId);
      if (option !== undefined) onPickOption?.(option);
      return;
    }
    const current = draftRef.current;
    onDraftChange(
      withAnswer(current, question.id, applyChoice(question, questionDraft(current, question.id), choice)),
    );
    clearAdvance();
    if (!question.multi && choice.kind !== "custom" && !isLast) {
      const from = index;
      advanceTimer.current = window.setTimeout(() => {
        advanceTimer.current = null;
        // Only from where the tap was: never a second step after a move.
        if (clampIndex(draftRef.current.index) !== from) return;
        goTo(from + 1);
      }, CLARIFY_AUTO_ADVANCE_MS);
    }
  };

  const typeCustom = (text: string): void => {
    if (!isCurrent(question.id)) return;
    const current = draftRef.current;
    onDraftChange(
      withAnswer(current, question.id, setCustomText(questionDraft(current, question.id), text, customMaxChars)),
    );
  };

  // Enter (or ➤) in «Свой ответ» is «Далее»: focus goes to the footer button,
  // where a press would leave it — on the last question it stays on «Готово»,
  // otherwise the next question takes it on mount. Handing it over here, inside
  // the key event, also blurs the typed field within that event rather than
  // mid-commit when the next question's rows take focus.
  const confirmCustom = (): void => {
    if (!isCurrent(question.id)) return;
    doneRef.current?.focus();
    if (!isLast) goTo(index + 1);
  };

  const finish = (): void => {
    clearAdvance();
    const answers = toAnswerInputs(round, draftRef.current);
    if (answers.length === 0) onSkip();
    else onSubmit(answers);
  };

  const skip = (): void => {
    clearAdvance();
    onSkip();
  };

  const dismiss = (): void => {
    clearAdvance();
    onDismiss();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (event.key !== "Escape" || event.nativeEvent.isComposing) return;
    event.preventDefault();
    dismiss();
  };

  const skipLabel =
    round.kind === "smart_rec_need"
      ? t("plan.clarifySkip")
      : round.kind === "smart_rec_facet"
        ? t("plan.facetSkip")
        : t("clarify.skipAll");

  return (
    <div className={skin.slot}>
      <motion.section
        ref={cardRef}
        role="region"
        aria-labelledby={legendId}
        data-testid="clarify-card"
        data-kind={round.kind}
        data-round-id={round.round_id}
        onKeyDown={onKeyDown}
        className={skin.card}
        {...cardMotion(reduced)}
      >
        <div className="flex shrink-0 items-center gap-1 px-2 pt-2">
          {total > 1 ? (
            <button
              type="button"
              onClick={() => goTo(index - 1)}
              disabled={index === 0}
              aria-label={t("clarify.back")}
              data-testid="clarify-back"
              className={skin.iconButton}
            >
              <ChevronLeft {...iconProps("md")} />
            </button>
          ) : null}
          <p className={skin.chip} data-testid="clarify-chip">
            {question.header.length > 0 ? <span className="uppercase">{question.header}</span> : null}
            {question.header.length > 0 && total > 1 ? " · " : null}
            {total > 1 ? t("clarify.progress", { n: index + 1, total }) : null}
          </p>
          {total > 1 ? (
            <button
              type="button"
              onClick={() => goTo(index + 1)}
              disabled={isLast}
              aria-label={t("clarify.next")}
              data-testid="clarify-forward"
              className={skin.iconButton}
            >
              <ChevronRight {...iconProps("md")} />
            </button>
          ) : null}
          <button
            type="button"
            onClick={dismiss}
            aria-label={t("clarify.close")}
            data-testid="clarify-close"
            className={skin.iconButton}
          >
            <X {...iconProps("md")} />
          </button>
        </div>

        {/* Long option lists (smart-rec R1 up to 12, R2 every catalogue option —
            index amendment A2) scroll here, inside the card's 70dvh cap. */}
        <div
          ref={scrollRef}
          data-testid="clarify-scroll"
          className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-3 pt-1"
        >
          {/* The outgoing and incoming questions share one grid cell while they
              cross-fade. Not `mode="popLayout"`: it pins the outgoing one with a
              <style> element appended to <head>, which the production build's
              CSP (index.html: style-src 'self' + Google Fonts, no
              'unsafe-inline', no nonce) refuses — the two would stack in flow
              and every question change would log a violation. Dev and the keyed
              QA build add 'unsafe-inline' (vite.config.ts), so only an unkeyed
              production build shows it. The incoming question comes later in the
              DOM, so it paints on top; until the outgoing one is gone the cell
              takes no pointer input and the outgoing slide is inert. */}
          <div className={moving ? "grid pointer-events-none" : "grid"}>
            <AnimatePresence initial={false} custom={direction} onExitComplete={() => setMoving(false)}>
              <QuestionSlide key={question.id} direction={direction} reduced={reduced}>
                <ClarifyQuestionView
                  question={question}
                  answer={questionDraft(draft, question.id)}
                  legendId={legendId}
                  skin={skin}
                  disabled={disabled}
                  focusOnMount={focusQuestion}
                  customMaxChars={customMaxChars}
                  onChoose={choose}
                  onCustomText={typeCustom}
                  onCustomConfirm={confirmCustom}
                />
              </QuestionSlide>
            </AnimatePresence>
          </div>
        </div>

        {round.allow_skip || !smartRec ? (
          <div className={skin.footer}>
            {round.allow_skip ? (
              <button
                type="button"
                onClick={skip}
                disabled={disabled}
                data-testid="clarify-skip-all"
                className={skin.skipButton}
              >
                {skipLabel}
              </button>
            ) : (
              <span aria-hidden="true" />
            )}
            {!smartRec ? (
              <button
                ref={doneRef}
                type="button"
                onClick={() => {
                  // Ignored while questions change: the second click of a
                  // double click on «Далее» would land on «Готово» and submit.
                  if (moving) return;
                  if (isLast) finish();
                  else goTo(index + 1);
                }}
                data-testid="clarify-next"
                data-last={isLast ? "true" : undefined}
                className={skin.primaryButton}
              >
                {isLast ? t("clarify.done") : t("clarify.next")}
              </button>
            ) : null}
          </div>
        ) : null}
      </motion.section>
      <p aria-live="polite" className="sr-only" data-testid="clarify-announce">
        {announcement}
      </p>
    </div>
  );
}

export default ClarifyCard;
